import { Dispatch, SetStateAction, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IDefaultFailureType } from "entities/FailureDetailsEntity/FailureDetailsEntityInterfaces";
import { IEditionSecondaryCodes } from "../../hooks/useSecondaryCodeRequirementsColumnsInterface";
import { IEventReportRow } from "../../SecondaryCodeRequerimentsInterfaces";
import { translateDefaultEntity } from "utilities/localesUtilities";
import { DefaultTranslationFieldsInTable } from "constants/constants";
import { DropdownSecondaryCodeRequirements, IOptionSecondaryCodes } from "../Dropdown";
import { IFailureCode } from "contexts/HMICachedDataContext/HMICachedDataContextInterfaces";
import { UserContext } from "contexts/UserContext/UserContext";
import { CircularProgress } from "@mui/material";
import { EventReportsEntity } from "entities/EventReports/EventReportsEntity";

export const FailureCodeDetailsDropdown = ({
    defaultFailureTypes,
    edit,
    setEdit,
    row,
}: {
    defaultFailureTypes: IDefaultFailureType[];
    edit: IEditionSecondaryCodes[];
    setEdit: Dispatch<SetStateAction<IEditionSecondaryCodes[]>>;
    row: IEventReportRow;
}) => {
    const { i18n } = useTranslation();
    const { user_id } = useContext(UserContext);

    const existingEdition = edit.findIndex((event) => event.id === row.event_report_id);
    const selectedFunctionalLocationId = edit[existingEdition]?.changes?.functional_location_id;
    const selectedFailureTypeId = edit[existingEdition]?.changes?.failure_type_id;
    const selectedFailureCodeId = edit[existingEdition]?.changes?.failure_code_id;

    const { loading, locationSpecificFailureCodes, nonLocationSpecificFailureCodes } =
        EventReportsEntity.GetLocationSpecificFailureCodesForSecondaryCodesEdition(
            row.machine_center.machine_center_id,
            selectedFunctionalLocationId,
        );

    const nonLocationSpecificOptions = useMemo(
        () =>
            nonLocationSpecificFailureCodes.map((failureCode) => ({
                codeId: failureCode.failure_code.failure_code_id,
                name: translateDefaultEntity(
                    i18n.language,
                    failureCode.failure_code as unknown as IFailureCode,
                    "name",
                    DefaultTranslationFieldsInTable.FAILURE_CODE,
                ),
                typeId: failureCode.failure_code.failure_type_id,
            })) || [],
        [nonLocationSpecificFailureCodes, i18n],
    );

    const locationSpecificOptions = useMemo(
        () =>
            locationSpecificFailureCodes.map((failureCode) => ({
                codeId: failureCode.failure_code.failure_code_id,
                name: translateDefaultEntity(
                    i18n.language,
                    failureCode.failure_code as unknown as IFailureCode,
                    "name",
                    DefaultTranslationFieldsInTable.FAILURE_CODE,
                ),
                typeId: failureCode.failure_code.failure_type_id,
            })),
        [locationSpecificFailureCodes, i18n],
    );

    const defaultOptions = useMemo(
        () =>
            defaultFailureTypes.reduce(
                (
                    accumulator: {
                        codeId: number;
                        name: string;
                        typeId: number;
                    }[],
                    failureType,
                ) => {
                    let failureCodesAccumulated = accumulator;
                    failureCodesAccumulated = [
                        ...failureCodesAccumulated,
                        ...failureType.failure_codes.map((failureCode) => ({
                            codeId: failureCode.failure_code_id,
                            name: translateDefaultEntity(
                                i18n.language,
                                failureCode as unknown as IFailureCode,
                                "name",
                                DefaultTranslationFieldsInTable.FAILURE_CODE,
                            ),
                            typeId: failureType.failure_type_id,
                        })),
                    ];

                    return failureCodesAccumulated;
                },
                [],
            ),
        [defaultFailureTypes, i18n],
    );

    const allFailureCodeOptions = useMemo(
        () => [...nonLocationSpecificOptions, ...locationSpecificOptions, ...defaultOptions],
        [locationSpecificOptions, defaultOptions, nonLocationSpecificOptions],
    );

    const failureCodesOptionsBasedOnSelection = useMemo(
        () =>
            allFailureCodeOptions.reduce((accumulator: IOptionSecondaryCodes[], failureCodeOption) => {
                let failureCodeOptionsAcc = accumulator;
                if (selectedFailureTypeId === failureCodeOption.typeId) {
                    if (!failureCodeOptionsAcc.find((failureCodeAcc) => failureCodeOption.codeId === failureCodeAcc.id))
                        failureCodeOptionsAcc = [
                            ...failureCodeOptionsAcc,
                            { id: failureCodeOption.codeId, name: failureCodeOption.name },
                        ];
                }
                return failureCodeOptionsAcc;
            }, []),
        [allFailureCodeOptions, selectedFailureTypeId],
    );

    const handleFailureCodeUpdate = async (id: number) => {
        if (existingEdition !== -1) {
            let editExisting = [...edit];
            editExisting[existingEdition] = {
                ...editExisting[existingEdition],
                changes: {
                    ...editExisting[existingEdition]?.changes,
                    failure_code_id: id,
                    secondary_failure_code_id: undefined,
                    ...(user_id && { edited_by_user_id: user_id }),
                },
            };
            setEdit(editExisting);
        }
    };

    return loading ? (
        <CircularProgress size={20} color="info" />
    ) : (
        <DropdownSecondaryCodeRequirements
            disabled={
                !selectedFailureTypeId || (edit[existingEdition]?.location_specific && !selectedFunctionalLocationId)
            }
            obligatory
            inlineStyle={{ width: "80px" }}
            optionSelected={selectedFailureCodeId}
            options={failureCodesOptionsBasedOnSelection}
            handleUpdate={handleFailureCodeUpdate}
        />
    );
};
